import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Select, DatePicker, message, Popconfirm, Collapse, Row, Col, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PlayCircleOutlined, StopOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { firmsApi } from '../api/firms';
import { dealersApi } from '../api/dealers';
import type { Firm, UpdateFirmRequest, AgentBackend } from '../types';
import { AGENT_BACKEND, AGENT_BACKEND_LABELS } from '../types';
import { MaskedValue } from '../components/MaskedValue';
import { ExportButton } from '../components/ExportButton';
import { formatDate } from '../utils/export';

export function Firms() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFirm, setEditingFirm] = useState<Firm | null>(null);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [deactivatingFirm, setDeactivatingFirm] = useState<Firm | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: firms = [], isLoading } = useQuery({
    queryKey: ['firms'],
    queryFn: firmsApi.getAll,
  });

  const { data: dealers = [] } = useQuery({
    queryKey: ['dealers'],
    queryFn: dealersApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: firmsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firms'] });
      message.success('Firma oluşturuldu');
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error('Firma oluşturulamadı'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFirmRequest }) =>
      firmsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firms'] });
      message.success('Firma güncellendi');
      setIsModalOpen(false);
      setEditingFirm(null);
      form.resetFields();
    },
    onError: () => message.error('Firma güncellenemedi'),
  });

  const deleteMutation = useMutation({
    mutationFn: firmsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firms'] });
      message.success('Firma silindi');
    },
    onError: () => message.error('Firma silinemedi'),
  });

  const activateMutation = useMutation({
    mutationFn: firmsApi.activateFirm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firms'] });
      message.success('Firma aktifleştirildi');
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Firma aktifleştirilemedi'),
  });

  const deactivateMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => firmsApi.deactivateFirm(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firms'] });
      message.success('Firma devre dışı bırakıldı');
      setIsDeactivateModalOpen(false);
      setDeactivatingFirm(null);
      setDeactivateReason('');
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Firma devre dışı bırakılamadı'),
  });

  const handleSubmit = (values: any) => {
    const data = {
      ...values,
      licenseExpiresAt: values.licenseExpiresAt?.toISOString(),
    };

    if (editingFirm) {
      updateMutation.mutate({ id: editingFirm.id, data: { ...data, isActive: editingFirm.isActive } });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (firm: Firm) => {
    setEditingFirm(firm);
    form.setFieldsValue({
      ...firm,
      licenseExpiresAt: firm.licenseExpiresAt ? dayjs(firm.licenseExpiresAt) : undefined,
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingFirm(null);
    form.resetFields();
    form.setFieldsValue({
      licenseExpiresAt: dayjs().add(1, 'year'),
    });
    setIsModalOpen(true);
  };

  const getLicenseStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'expired': return 'red';
      case 'deactivated': return 'default';
      default: return 'default';
    }
  };

  const getLicenseStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'expired': return 'Süresi Doldu';
      case 'deactivated': return 'Devre Dışı';
      default: return status;
    }
  };

  const isExpiringSoon = (firm: Firm) => {
    if (!firm.licenseExpiresAt) return false;
    const daysLeft = dayjs(firm.licenseExpiresAt).diff(dayjs(), 'day');
    return daysLeft <= 30 && daysLeft > 0;
  };

  const columns = [
    {
      title: 'Firma Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Firm) => (
        <Button type="link" onClick={() => navigate(`/firms/${record.id}`)}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Subdomain',
      dataIndex: 'subdomain',
      key: 'subdomain',
      render: (subdomain: string) => (
        <code>{subdomain}.raporgor.com</code>
      ),
    },
    {
      title: 'Bayi',
      dataIndex: 'dealerName',
      key: 'dealerName',
      render: (name: string | null) => name || <Tag>Doğrudan Müşteri</Tag>,
    },
    {
      title: 'Lisans Durumu',
      key: 'licenseStatus',
      render: (_: unknown, record: Firm) => (
        <Space direction="vertical" size={0}>
          <Tag color={getLicenseStatusColor(record.licenseStatus)}>
            {getLicenseStatusText(record.licenseStatus)}
          </Tag>
          {record.licenseExpiresAt && (
            <span style={{ fontSize: '12px', color: isExpiringSoon(record) ? '#faad14' : '#888' }}>
              {dayjs(record.licenseExpiresAt).format('DD.MM.YYYY')}
              {isExpiringSoon(record) && ' (Yakında Bitiyor)'}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Backend',
      dataIndex: 'agentBackend',
      key: 'agentBackend',
      render: (backend: AgentBackend) => (
        <Tag color={backend === AGENT_BACKEND.IttihatAgent ? 'purple' : 'blue'}>
          {AGENT_BACKEND_LABELS[backend]}
        </Tag>
      ),
    },
    {
      title: 'License Key',
      dataIndex: 'licenseKey',
      key: 'licenseKey',
      render: (key: string) => <MaskedValue value={key} />,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: Firm) => (
        <Space>
          <Tooltip title="Detay">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/firms/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Düzenle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          {record.licenseStatus !== 'deactivated' && (
            <Tooltip title="Devre Dışı Bırak">
              <Button
                type="text"
                danger
                icon={<StopOutlined />}
                onClick={() => {
                  setDeactivatingFirm(record);
                  setIsDeactivateModalOpen(true);
                }}
              />
            </Tooltip>
          )}
          {record.licenseStatus === 'deactivated' && (
            <Tooltip title="Aktifleştir">
              <Popconfirm
                title="Firmayı tekrar aktifleştirmek istediğinize emin misiniz?"
                onConfirm={() => activateMutation.mutate(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button type="text" icon={<PlayCircleOutlined />} style={{ color: 'green' }} />
              </Popconfirm>
            </Tooltip>
          )}
          <Popconfirm
            title="Firmayı silmek istediğinize emin misiniz?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Firmalar</h2>
        <Space>
          <ExportButton
            data={firms}
            filename="firmalar"
            columns={[
              { key: 'id', title: 'ID' },
              { key: 'name', title: 'Firma Adı' },
              { key: 'subdomain', title: 'Subdomain' },
              { key: 'dealerName', title: 'Bayi', render: (v) => v || 'Doğrudan Müşteri' },
              { key: 'licenseStatus', title: 'Lisans Durumu', render: (v) => getLicenseStatusText(v) },
              { key: 'licenseExpiresAt', title: 'Lisans Bitiş', render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '' },
              { key: 'contactEmail', title: 'E-posta' },
              { key: 'phone', title: 'Telefon' },
              { key: 'createdAt', title: 'Oluşturulma', render: formatDate },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Yeni Firma
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={firms}
        rowKey="id"
        loading={isLoading}
      />

      <Modal
        title={editingFirm ? 'Firma Düzenle' : 'Yeni Firma'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingFirm(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Firma Adı"
                rules={[{ required: true, message: 'Firma adı gerekli' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="subdomain"
                label="Subdomain"
                rules={[
                  { required: true, message: 'Subdomain gerekli' },
                  { pattern: /^[a-z0-9-]+$/, message: 'Sadece küçük harf, rakam ve tire kullanın' }
                ]}
                extra="Örnek: abc-tekstil → abc-tekstil.raporgor.com"
              >
                <Input disabled={!!editingFirm} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dealerId"
                label="Bayi"
              >
                <Select allowClear placeholder="Doğrudan Müşteri">
                  {dealers.filter(d => d.isActive).map((dealer) => (
                    <Select.Option key={dealer.id} value={dealer.id}>
                      {dealer.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="licenseExpiresAt"
                label="Lisans Bitiş Tarihi"
              >
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contactEmail"
                label="İletişim E-postası"
                rules={[{ type: 'email', message: 'Geçerli bir e-posta girin' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Telefon"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {editingFirm && (
            <Form.Item
              name="agentBackend"
              label="Agent Backend"
              tooltip="Sorgu yönlendirme: RaporGor Agent (SignalR / on-prem .NET) veya İttihat Hub (mTLS+JWT proxy)"
            >
              <Select>
                <Select.Option value={AGENT_BACKEND.RaporGorAgent}>
                  {AGENT_BACKEND_LABELS[AGENT_BACKEND.RaporGorAgent]}
                </Select.Option>
                <Select.Option value={AGENT_BACKEND.IttihatAgent}>
                  {AGENT_BACKEND_LABELS[AGENT_BACKEND.IttihatAgent]}
                </Select.Option>
              </Select>
            </Form.Item>
          )}

          <Collapse ghost items={[{
            key: 'official',
            label: 'Resmi Bilgiler',
            children: (
              <>
                <Form.Item name="officialName" label="Resmi Firma Adı">
                  <Input />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="taxNumber" label="Vergi No">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="taxOffice" label="Vergi Dairesi">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="city" label="Şehir">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="district" label="İlçe">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="address" label="Adres">
                  <Input.TextArea rows={2} />
                </Form.Item>

                <Form.Item name="postalCode" label="Posta Kodu">
                  <Input style={{ width: 150 }} />
                </Form.Item>

                <Form.Item name="notes" label="Notlar">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </>
            ),
          }]} />

          <Form.Item style={{ marginTop: 16 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingFirm ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Deactivate Modal */}
      <Modal
        title="Firma Devre Dışı Bırak"
        open={isDeactivateModalOpen}
        onCancel={() => {
          setIsDeactivateModalOpen(false);
          setDeactivatingFirm(null);
          setDeactivateReason('');
        }}
        onOk={() => {
          if (deactivatingFirm) {
            deactivateMutation.mutate({ id: deactivatingFirm.id, reason: deactivateReason || undefined });
          }
        }}
        okText="Devre Dışı Bırak"
        okButtonProps={{ danger: true, loading: deactivateMutation.isPending }}
        cancelText="İptal"
      >
        <p>
          <strong>{deactivatingFirm?.name}</strong> firmasını devre dışı bırakmak istediğinize emin misiniz?
        </p>
        <p style={{ color: '#888' }}>
          Firma devre dışı bırakıldığında kullanıcılar sisteme giriş yapamayacak.
        </p>
        <Form.Item label="Sebep (Opsiyonel)">
          <Input.TextArea
            rows={3}
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            placeholder="Devre dışı bırakma sebebini girebilirsiniz..."
          />
        </Form.Item>
      </Modal>
    </div>
  );
}
